import type {
  FillExtrusionLayerSpecification,
  HillshadeLayerSpecification,
  LayerSpecification,
  RasterDEMSourceSpecification,
  RasterLayerSpecification,
  RasterSourceSpecification,
  SourceSpecification,
  StyleSpecification,
  TerrainSpecification,
} from 'maplibre-gl'

import { buildMapStyle } from '@/lib/map-style'
import type { Theme } from '@/store/activity-store'

/**
 * Data-driven registry of map basemaps and overlays. Every on-map layer
 * option (the layer menu, the style builder, persistence validation) is
 * derived from `MAP_LAYERS`, so adding or removing a tile source is a
 * one-entry change here.
 *
 * Pure: no DOM, no MapLibre runtime import (types only), so every builder
 * below is unit-testable under jsdom without WebGL.
 *
 * Privacy: nothing in this file triggers a request by itself. A provider
 * is only contacted once the map renders a style/layer built from its
 * entry, i.e. once the user has selected it (CARTO is the default
 * basemap; every other entry is opt-in). See `SECURITY.md`, which lists
 * every endpoint below -- keep the two in sync.
 *
 * Usage policies: OSM, openmaps.fr and Waymarked Trails forbid bulk
 * download/prefetch, so nothing here prefetches or caches tiles for
 * offline use -- tiles are only requested for the visible viewport.
 * Entries/URLs/attributions were verified by the coordinator (2026-10-26);
 * none come from gpx.studio-hosted infrastructure.
 */

export type MapLayerGroup = 'map' | 'satellite' | 'overlay'

/** Named API keys an entry can require; resolved from build-time env vars (`getMapApiKeys`). */
export type MapApiKeyName = 'arcgis' | 'maptiler'
export type MapApiKeys = Partial<Record<MapApiKeyName, string>>

interface MapLayerEntryBase {
  id: string
  group: MapLayerGroup
  label: string
  /** Licensed for non-commercial use only -- surfaced as a note in the layer menu. */
  nonCommercial: boolean
  /** Extra short usage caveat shown under the entry in the layer menu. */
  licenseNote?: string
  /** If set, the entry is only offered when this API key is configured. */
  requiresKey?: MapApiKeyName
  /** The basemap already draws hillshading, so 3D mode doesn't add a second hillshade layer. */
  providesHillshade?: boolean
  /**
   * Regional coverage, e.g. "Switzerland only". Set only for sources that
   * serve tiles for part of the world; the layer menu shows it in the
   * entry's note and lists the entry under a "Regional" subheading.
   */
  coverage?: string
  /**
   * HTML attribution shown in MapLibre's attribution control while this
   * basemap is active, *in addition to* the attribution its sources carry
   * themselves (e.g. MapTiler's TileJSON/style attribution, which is only
   * known at runtime and must not be overridden).
   */
  attribution?: string
  /** The provider's terms require its logo on the map while this basemap is shown (rendered by `ActivityMap`). */
  logo?: MapProviderLogo
}

/** Providers whose logo the map must show while their basemap is active. */
export type MapProviderLogo = 'maptiler'

/** 3D-building extrusion config for a vector style whose tiles carry building heights but whose style doesn't extrude them itself. */
export interface BuildingsConfig {
  /** Source id *inside the published style* that carries the building polygons. */
  source: string
  sourceLayer: string
  heightProperty: string
  minHeightProperty: string
}

/** `[west, south, east, north]` in degrees. */
export type LngLatBBox = [number, number, number, number]

/** One XYZ raster tile source. `{apiKey}` in a URL is replaced with the (URL-encoded) key named by the entry's `requiresKey`. */
export interface RasterXyzTileSource {
  tiles: string[]
  tileSize: number
  minzoom?: number
  /** Highest zoom the provider serves; MapLibre overzooms beyond it. */
  maxzoom: number
  /** HTML attribution shown in MapLibre's attribution control while the source is on the map. */
  attribution: string
  /** Coverage of a regional source: MapLibre requests no tiles outside it. */
  bounds?: LngLatBBox
}

/**
 * A raster source described by a TileJSON document (`url`, `{apiKey}`
 * substituted as above): MapLibre fetches it and takes tiles, zoom range,
 * tile size, bounds and attribution from it. Nothing is overridden here,
 * so the provider's own attribution is the one shown.
 */
export interface RasterTileJsonSource {
  url: string
}

export type RasterTileSource = RasterXyzTileSource | RasterTileJsonSource

export interface VectorStyleLayerEntry extends MapLayerEntryBase {
  kind: 'vector-style'
  /** Published MapLibre style URL; may differ per app theme. Attribution comes from the style's own sources. */
  styleUrl: (theme: Theme) => string
  /** Pure post-fetch modification of the published style (applied via MapLibre's `transformStyle`). */
  transform?: (style: StyleSpecification, keys: MapApiKeys) => StyleSpecification
  buildings?: BuildingsConfig
}

export interface RasterLayerEntry extends MapLayerEntryBase {
  kind: 'raster'
  /** Raster sources stacked bottom-to-top (e.g. imagery, then a label overlay). */
  sources: RasterTileSource[]
}

export type MapLayerEntry = VectorStyleLayerEntry | RasterLayerEntry

const OSM_COPYRIGHT_LINK =
  '<a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a>'

export const MAPTERHORN_ATTRIBUTION =
  '<a href="https://mapterhorn.com/attribution" target="_blank" rel="noopener noreferrer">© Mapterhorn</a>'

export const MAPTERHORN_TILES = 'https://tiles.mapterhorn.com/{z}/{x}/{y}.webp'

const LIBERTY_STYLE_URL = 'https://tiles.openfreemap.org/styles/liberty'

const MAPTILER_ATTRIBUTION = `© <a href="https://www.maptiler.com/copyright/" target="_blank" rel="noopener noreferrer">MapTiler</a> © ${OSM_COPYRIGHT_LINK} contributors`

const MAPTILER_LICENSE_NOTE =
  'MapTiler free plan: non-commercial, logo required; paid plans remove these limits'

// satellite-v2 (what gpx.studio uses): `satellite-v4/tiles.json` answers 404.
const MAPTILER_SATELLITE: RasterXyzTileSource = {
  tiles: ['https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key={apiKey}'],
  tileSize: 256,
  maxzoom: 19,
  attribution: MAPTILER_ATTRIBUTION,
}

function expandSubdomains(template: string, subdomains: readonly string[]): string[] {
  return subdomains.map((s) => template.replace('{s}', s))
}

export const BASEMAP_HILLSHADE_SOURCE_ID = 'basemap-hillshade-dem'
export const BASEMAP_HILLSHADE_LAYER_ID = 'basemap-hillshade'
export const LIBERTY_SATELLITE_SOURCE_ID = 'basemap-satellite'

/** Source-layers whose (line/symbol/fill) layers must stay *above* the hillshade in Liberty Topo. */
const ABOVE_HILLSHADE_SOURCE_LAYERS = new Set([
  'waterway',
  'water',
  'transportation',
  'building',
  'boundary',
])

function hillshadePaint(theme: Theme): NonNullable<HillshadeLayerSpecification['paint']> {
  return {
    'hillshade-exaggeration': 0.35,
    'hillshade-shadow-color': theme === 'dark' ? '#000000' : '#473b24',
    'hillshade-highlight-color': theme === 'dark' ? '#3f3f46' : '#ffffff',
  }
}

/**
 * Liberty Topo: inserts a Mapterhorn hillshade layer directly below the
 * first water/road/building/boundary/label layer, i.e. above Liberty's
 * background, landcover and landuse fills but under everything a reader
 * needs to see.
 */
export function addHillshadeToStyle(style: StyleSpecification): StyleSpecification {
  const hillshade: HillshadeLayerSpecification = {
    id: BASEMAP_HILLSHADE_LAYER_ID,
    type: 'hillshade',
    source: BASEMAP_HILLSHADE_SOURCE_ID,
    paint: hillshadePaint('light'),
  }
  const insertAt = style.layers.findIndex(
    (l) =>
      l.type === 'symbol' ||
      ('source-layer' in l &&
        typeof l['source-layer'] === 'string' &&
        ABOVE_HILLSHADE_SOURCE_LAYERS.has(l['source-layer'])),
  )
  const layers = [...style.layers]
  layers.splice(insertAt === -1 ? layers.length : insertAt, 0, hillshade)
  return {
    ...style,
    sources: {
      ...style.sources,
      [BASEMAP_HILLSHADE_SOURCE_ID]: buildTerrainDemSource(),
    },
    layers,
  }
}

/** Layer types kept from Liberty in the satellite hybrid: roads, boundaries and labels. */
const SATELLITE_HYBRID_LAYER_TYPES = new Set<LayerSpecification['type']>(['line', 'symbol'])

/**
 * Liberty Satellite: MapTiler satellite imagery underneath, Liberty's
 * road/boundary/label layers on top. Drops Liberty's background, raster
 * relief, every fill (landcover/landuse/water/building) and the
 * 3D-building extrusion so the imagery shows through.
 */
export function libertySatelliteStyle(
  style: StyleSpecification,
  keys: MapApiKeys,
): StyleSpecification {
  const key = keys.maptiler
  if (key == null) throw new Error('Liberty Satellite requires the maptiler key.')
  const kept = style.layers.filter((l) => SATELLITE_HYBRID_LAYER_TYPES.has(l.type))
  const imagery: RasterLayerSpecification = {
    id: LIBERTY_SATELLITE_SOURCE_ID,
    type: 'raster',
    source: LIBERTY_SATELLITE_SOURCE_ID,
  }
  return {
    ...style,
    sources: {
      ...style.sources,
      [LIBERTY_SATELLITE_SOURCE_ID]: rasterSourceSpec(MAPTILER_SATELLITE, (t) =>
        t.replaceAll('{apiKey}', encodeURIComponent(key)),
      ),
    },
    layers: [imagery, ...kept],
  }
}

export const MAP_LAYERS: readonly MapLayerEntry[] = [
  // --- Map ------------------------------------------------------------------
  {
    id: 'carto',
    group: 'map',
    label: 'CARTO',
    kind: 'vector-style',
    nonCommercial: false,
    styleUrl: buildMapStyle,
    // Verified (2026-10-26) against the live style + a z15 city-centre
    // tile: CARTO's current style uses a Protomaps-schema vector source
    // (`protomaps`) whose `buildings` source-layer carries `height` /
    // `min_height` attributes, but the style itself only draws flat fills.
    buildings: {
      source: 'protomaps',
      sourceLayer: 'buildings',
      heightProperty: 'height',
      minHeightProperty: 'min_height',
    },
  },
  {
    // Liberty ships its own `building-3d` fill-extrusion layer, so no
    // `buildings` config is needed for 3D buildings.
    id: 'liberty',
    group: 'map',
    label: 'Liberty (OpenFreeMap)',
    kind: 'vector-style',
    nonCommercial: false,
    styleUrl: () => LIBERTY_STYLE_URL,
  },
  {
    id: 'liberty-topo',
    group: 'map',
    label: 'Liberty Topo',
    kind: 'vector-style',
    nonCommercial: false,
    providesHillshade: true,
    styleUrl: () => LIBERTY_STYLE_URL,
    transform: addHillshadeToStyle,
  },
  {
    id: 'osm',
    group: 'map',
    label: 'OpenStreetMap',
    kind: 'raster',
    nonCommercial: false,
    sources: [
      {
        tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
        tileSize: 256,
        maxzoom: 19,
        attribution: `© ${OSM_COPYRIGHT_LINK} contributors`,
      },
    ],
  },
  {
    id: 'opentopomap',
    group: 'map',
    label: 'OpenTopoMap',
    kind: 'raster',
    nonCommercial: false,
    sources: [
      {
        tiles: expandSubdomains('https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', [
          'a',
          'b',
          'c',
        ]),
        tileSize: 256,
        maxzoom: 17,
        attribution: `Map data: © ${OSM_COPYRIGHT_LINK} contributors, SRTM | Map style: © <a href="https://opentopomap.org" target="_blank" rel="noopener noreferrer">OpenTopoMap</a> (CC-BY-SA)`,
      },
    ],
  },
  {
    id: 'cyclosm',
    group: 'map',
    label: 'CyclOSM',
    kind: 'raster',
    nonCommercial: false,
    sources: [
      {
        tiles: expandSubdomains(
          'https://{s}.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png',
          ['a', 'b', 'c'],
        ),
        tileSize: 256,
        maxzoom: 17,
        attribution: `© <a href="https://www.cyclosm.org" target="_blank" rel="noopener noreferrer">CyclOSM</a> © ${OSM_COPYRIGHT_LINK} contributors`,
      },
    ],
  },
  {
    id: 'openhikingmap',
    group: 'map',
    label: 'OpenHikingMap',
    kind: 'raster',
    nonCommercial: true,
    licenseNote: 'Shared 500k tiles/month cap',
    sources: [
      {
        tiles: ['https://tile.openmaps.fr/openhikingmap/{z}/{x}/{y}.png'],
        tileSize: 256,
        maxzoom: 18,
        attribution: `© <a href="https://openmaps.fr" target="_blank" rel="noopener noreferrer">OpenHikingMap</a> © ${OSM_COPYRIGHT_LINK} contributors`,
      },
    ],
  },
  // --- Satellite ------------------------------------------------------------
  {
    id: 'esri-world-imagery',
    group: 'satellite',
    label: 'Esri Satellite',
    kind: 'raster',
    nonCommercial: false,
    requiresKey: 'arcgis',
    licenseNote: 'Esri World Imagery',
    sources: [
      {
        // ArcGIS Location Platform basemap endpoint (API key required; an
        // unauthenticated request returns `499 Token Required`). Never the
        // keyless services.arcgisonline.com endpoint.
        tiles: [
          'https://ibasemaps-api.arcgis.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}?token={apiKey}',
        ],
        tileSize: 256,
        maxzoom: 19,
        attribution:
          'Powered by <a href="https://www.esri.com" target="_blank" rel="noopener noreferrer">Esri</a> | Esri, Maxar, Earthstar Geographics, and the GIS User Community',
      },
    ],
  },
  {
    // MapTiler's free plan: non-commercial only, MapTiler logo required on
    // the map (`logo`). The TileJSON supplies tiles/zoom range/tile size
    // and MapTiler's own attribution.
    id: 'maptiler-satellite',
    group: 'satellite',
    label: 'MapTiler Satellite',
    kind: 'raster',
    nonCommercial: true,
    requiresKey: 'maptiler',
    licenseNote: MAPTILER_LICENSE_NOTE,
    attribution: MAPTILER_ATTRIBUTION,
    logo: 'maptiler',
    sources: [MAPTILER_SATELLITE],
  },
  {
    // Fetched and re-styled like the Liberty styles: the app's route and
    // markers are re-installed on top after the switch.
    id: 'maptiler-hybrid',
    group: 'satellite',
    label: 'MapTiler Hybrid',
    kind: 'vector-style',
    nonCommercial: true,
    requiresKey: 'maptiler',
    licenseNote: `Satellite + labels · ${MAPTILER_LICENSE_NOTE}`,
    attribution: MAPTILER_ATTRIBUTION,
    logo: 'maptiler',
    styleUrl: () => 'https://api.maptiler.com/maps/hybrid/style.json?key={apiKey}',
  },
  {
    id: 'liberty-satellite',
    group: 'satellite',
    label: 'MapTiler Satellite + Liberty',
    kind: 'vector-style',
    nonCommercial: true,
    requiresKey: 'maptiler',
    licenseNote: `Satellite + OpenFreeMap Liberty roads/labels · ${MAPTILER_LICENSE_NOTE}`,
    attribution: MAPTILER_ATTRIBUTION,
    logo: 'maptiler',
    styleUrl: () => LIBERTY_STYLE_URL,
    transform: libertySatelliteStyle,
  },
  // Regional aerial photography: each source has `bounds`, so no tile is
  // requested outside its coverage (the map is blank there).
  {
    id: 'basemap-at-orthofoto',
    group: 'satellite',
    label: 'basemap.at Orthofoto',
    kind: 'raster',
    nonCommercial: false,
    coverage: 'Austria only',
    licenseNote: 'basemap.at (CC BY 4.0)',
    sources: [
      {
        tiles: [
          'https://maps.wien.gv.at/basemap/bmaporthofoto30cm/normal/google3857/{z}/{y}/{x}.jpeg',
        ],
        tileSize: 256,
        maxzoom: 19,
        bounds: [9.5, 46.35, 17.2, 49.05],
        attribution:
          'Datenquelle: <a href="https://basemap.at" target="_blank" rel="noopener noreferrer">basemap.at</a>',
      },
    ],
  },
  {
    id: 'ign-orthophotos',
    group: 'satellite',
    label: 'IGN Orthophotos',
    kind: 'raster',
    nonCommercial: false,
    coverage: 'Metropolitan France only',
    licenseNote: 'IGN Géoplateforme (Licence Ouverte 2.0)',
    sources: [
      {
        tiles: [
          'https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=ORTHOIMAGERY.ORTHOPHOTOS&STYLE=normal&TILEMATRIXSET=PM&FORMAT=image/jpeg&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}',
        ],
        tileSize: 256,
        maxzoom: 19,
        bounds: [-5.3, 41.3, 9.7, 51.2],
        attribution:
          '© <a href="https://www.ign.fr" target="_blank" rel="noopener noreferrer">IGN</a> – Géoplateforme (Licence Ouverte 2.0)',
      },
    ],
  },
  {
    id: 'swisstopo-swissimage',
    group: 'satellite',
    label: 'SWISSIMAGE',
    kind: 'raster',
    nonCommercial: false,
    coverage: 'Switzerland only',
    licenseNote: 'swisstopo open data',
    sources: [
      {
        // Note the {x}/{y} order here (unlike the WMTS-style URLs above).
        tiles: [
          'https://wmts.geo.admin.ch/1.0.0/ch.swisstopo.swissimage/default/current/3857/{z}/{x}/{y}.jpeg',
        ],
        tileSize: 256,
        maxzoom: 20,
        bounds: [5.9, 45.8, 10.5, 47.85],
        attribution:
          '© <a href="https://www.swisstopo.admin.ch" target="_blank" rel="noopener noreferrer">swisstopo</a>',
      },
    ],
  },
  {
    id: 'usgs-imagery',
    group: 'satellite',
    label: 'USGS Imagery',
    kind: 'raster',
    nonCommercial: false,
    coverage: 'United States only',
    licenseNote: 'USGS (public domain)',
    sources: [
      {
        tiles: [
          'https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryOnly/MapServer/tile/{z}/{y}/{x}',
        ],
        tileSize: 256,
        maxzoom: 16,
        // One box around CONUS, Alaska and Hawaii.
        bounds: [-179.2, 18.9, -66.9, 71.4],
        attribution: 'USGS The National Map: Orthoimagery',
      },
    ],
  },
  // --- Overlays (transparent PNG, drawn over any basemap) -------------------
  ...(
    [
      ['hiking', 'Hiking trails'],
      ['cycling', 'Cycling routes'],
      ['mtb', 'MTB trails'],
    ] as const
  ).map(([route, label]): RasterLayerEntry => ({
    id: `waymarked-${route}`,
    group: 'overlay',
    label,
    kind: 'raster',
    nonCommercial: true,
    licenseNote: 'Waymarked Trails',
    sources: [
      {
        tiles: [`https://tile.waymarkedtrails.org/${route}/{z}/{x}/{y}.png`],
        tileSize: 256,
        maxzoom: 18,
        attribution: `© <a href="https://waymarkedtrails.org" target="_blank" rel="noopener noreferrer">Waymarked Trails</a> (CC BY-SA 3.0 DE), © ${OSM_COPYRIGHT_LINK} contributors`,
      },
    ],
  })),
]

export const DEFAULT_BASEMAP_ID = 'carto'

export interface MapLayerPreferences {
  basemapId: string
  overlayIds: string[]
  terrain3d: boolean
}

export const DEFAULT_MAP_LAYER_PREFERENCES: MapLayerPreferences = {
  basemapId: DEFAULT_BASEMAP_ID,
  overlayIds: [],
  terrain3d: false,
}

/** Reads build-time keys. Called at use time (not module load) so tests can stub env vars. */
export function getMapApiKeys(): MapApiKeys {
  const keys: MapApiKeys = {}
  const arcgis = import.meta.env.VITE_ARCGIS_API_KEY?.trim()
  if (arcgis != null && arcgis !== '') keys.arcgis = arcgis
  const maptiler = import.meta.env.VITE_MAPTILER_API_KEY?.trim()
  if (maptiler != null && maptiler !== '') keys.maptiler = maptiler
  return keys
}

export function isLayerAvailable(entry: MapLayerEntry, keys: MapApiKeys): boolean {
  return entry.requiresKey == null || keys[entry.requiresKey] != null
}

export function getAvailableLayers(
  keys: MapApiKeys,
  registry: readonly MapLayerEntry[] = MAP_LAYERS,
): MapLayerEntry[] {
  return registry.filter((entry) => isLayerAvailable(entry, keys))
}

/** The short license/usage line shown under an entry in the layer menu, if any. */
export function describeLayerLicense(entry: MapLayerEntry): string | undefined {
  const parts = [entry.nonCommercial ? 'Non-commercial license' : undefined, entry.licenseNote]
  const text = parts.filter((p): p is string => p != null).join(' · ')
  return text === '' ? undefined : text
}

/** The full note shown under an entry in the layer menu: regional coverage first, then license/usage. */
export function describeLayer(entry: MapLayerEntry): string | undefined {
  const parts = [entry.coverage, describeLayerLicense(entry)]
  const text = parts.filter((p): p is string => p != null).join(' · ')
  return text === '' ? undefined : text
}

/** One menu group's entries split into worldwide and regional (`coverage`) ones, registry order kept. */
export function partitionByCoverage(entries: readonly MapLayerEntry[]): {
  global: MapLayerEntry[]
  regional: MapLayerEntry[]
} {
  return {
    global: entries.filter((e) => e.coverage == null),
    regional: entries.filter((e) => e.coverage != null),
  }
}

export interface ResolvedMapLayers {
  basemap: MapLayerEntry
  overlays: RasterLayerEntry[]
  terrain3d: boolean
}

/**
 * Resolves persisted preferences against the registry and the configured
 * keys: an unknown/removed/unavailable basemap falls back to the default,
 * and unknown/unavailable overlay ids are dropped. Never throws on stale
 * persisted data.
 */
export function resolveMapLayers(
  prefs: MapLayerPreferences | undefined,
  keys: MapApiKeys,
  registry: readonly MapLayerEntry[] = MAP_LAYERS,
): ResolvedMapLayers {
  const available = getAvailableLayers(keys, registry)
  const basemaps = available.filter((e) => e.group !== 'overlay')
  const basemap =
    basemaps.find((e) => e.id === prefs?.basemapId) ??
    basemaps.find((e) => e.id === DEFAULT_BASEMAP_ID) ??
    basemaps[0]
  if (basemap == null) throw new Error('Map layer registry has no available basemap.')
  const overlayIds = new Set(prefs?.overlayIds ?? [])
  const overlays = available.filter(
    (e): e is RasterLayerEntry =>
      e.group === 'overlay' && e.kind === 'raster' && overlayIds.has(e.id),
  )
  return { basemap, overlays, terrain3d: prefs?.terrain3d === true }
}

function substituteKey(template: string, entry: MapLayerEntry, keys: MapApiKeys): string {
  if (entry.requiresKey == null) return template
  const key = keys[entry.requiresKey]
  if (key == null) throw new Error(`Map layer "${entry.id}" requires the ${entry.requiresKey} key.`)
  return template.replaceAll('{apiKey}', encodeURIComponent(key))
}

function rasterSourceSpec(
  source: RasterTileSource,
  substitute: (template: string) => string = (t) => t,
): RasterSourceSpecification {
  if ('url' in source) return { type: 'raster', url: substitute(source.url) }
  return {
    type: 'raster',
    tiles: source.tiles.map(substitute),
    tileSize: source.tileSize,
    ...(source.minzoom != null ? { minzoom: source.minzoom } : {}),
    maxzoom: source.maxzoom,
    ...(source.bounds != null ? { bounds: source.bounds } : {}),
    attribution: source.attribution,
  }
}

/** MapLibre source specs for a raster entry, bottom-to-top, with API keys substituted. */
export function buildRasterSources(
  entry: RasterLayerEntry,
  keys: MapApiKeys,
): RasterSourceSpecification[] {
  return entry.sources.map((source) =>
    rasterSourceSpec(source, (template) => substituteKey(template, entry, keys)),
  )
}

export const BASEMAP_SOURCE_ID = 'basemap-raster'

/** Per-entry source/layer id for the i-th raster source of `entry`. */
function rasterLayerId(prefix: string, index: number): string {
  return index === 0 ? prefix : `${prefix}-${index}`
}

/** What to hand MapLibre's `setStyle(style, { transformStyle })` for a basemap. */
export interface BaseStyle {
  style: string | StyleSpecification
  transformStyle?: (
    previous: StyleSpecification | undefined,
    next: StyleSpecification,
  ) => StyleSpecification
}

/**
 * The style for a basemap: a published style URL for vector-style entries
 * (MapLibre fetches it; `transformStyle` applies the entry's own
 * modification, e.g. Liberty Topo's hillshade), or a minimal raster style
 * for raster entries. No glyphs/sprite needed for raster styles: the
 * app's own layers are all line/circle layers.
 */
export function buildBaseStyle(entry: MapLayerEntry, theme: Theme, keys: MapApiKeys): BaseStyle {
  if (entry.kind === 'vector-style') {
    const transform = entry.transform
    return {
      style: substituteKey(entry.styleUrl(theme), entry, keys),
      ...(transform != null ? { transformStyle: (_prev, next) => transform(next, keys) } : {}),
    }
  }
  const specs = buildRasterSources(entry, keys)
  const ids = specs.map((_, i) => rasterLayerId(BASEMAP_SOURCE_ID, i))
  return {
    style: {
      version: 8,
      sources: Object.fromEntries(specs.map((spec, i) => [ids[i], spec])),
      layers: ids.map((id): RasterLayerSpecification => ({ id, type: 'raster', source: id })),
    },
  }
}

/** A source (and optionally a layer) the app adds on top of the basemap style, below its own activity layers. */
export interface MapExtra {
  sourceId: string
  /** `null` when the layer uses a source that already belongs to the basemap style (e.g. 3D buildings). */
  source: SourceSpecification | null
  layer: LayerSpecification | null
}

export const TERRAIN_DEM_SOURCE_ID = 'terrain-dem'
export const HILLSHADE_DEM_SOURCE_ID = 'hillshade-dem'
export const HILLSHADE_LAYER_ID = 'terrain-hillshade'
export const BUILDINGS_LAYER_ID = 'buildings-3d'
export const TERRAIN_EXAGGERATION = 1.3
export const TERRAIN_PITCH = 60

export function overlayLayerId(entryId: string): string {
  return `overlay-${entryId}`
}

export function buildTerrainDemSource(): RasterDEMSourceSpecification {
  return {
    type: 'raster-dem',
    tiles: [MAPTERHORN_TILES],
    encoding: 'terrarium',
    tileSize: 512,
    maxzoom: 17,
    attribution: MAPTERHORN_ATTRIBUTION,
  }
}

export function buildTerrainSpecification(): TerrainSpecification {
  return { source: TERRAIN_DEM_SOURCE_ID, exaggeration: TERRAIN_EXAGGERATION }
}

export function buildHillshadeLayer(theme: Theme): HillshadeLayerSpecification {
  return {
    id: HILLSHADE_LAYER_ID,
    type: 'hillshade',
    // A separate DEM source from the terrain mesh, per MapLibre's own
    // guidance (sharing one raster-dem source between terrain and
    // hillshade degrades tile caching/rendering).
    source: HILLSHADE_DEM_SOURCE_ID,
    paint: hillshadePaint(theme),
  }
}

export function buildBuildingsLayer(
  config: BuildingsConfig,
  theme: Theme,
): FillExtrusionLayerSpecification {
  return {
    id: BUILDINGS_LAYER_ID,
    type: 'fill-extrusion',
    source: config.source,
    'source-layer': config.sourceLayer,
    minzoom: 14,
    paint: {
      'fill-extrusion-color': theme === 'dark' ? '#3f3f46' : '#d4d4d8',
      'fill-extrusion-height': ['coalesce', ['get', config.heightProperty], 0],
      'fill-extrusion-base': ['coalesce', ['get', config.minHeightProperty], 0],
      'fill-extrusion-opacity': 0.8,
    },
  }
}

/**
 * Everything the app adds above the basemap style and below its own
 * activity layers, bottom-to-top: terrain DEM (source only), hillshade
 * (unless the basemap already has one), overlays, then 3D buildings (only
 * when the basemap's vector tiles carry heights its style doesn't already
 * extrude). The terrain mesh itself is returned separately since it's set
 * via `map.setTerrain`, not a layer.
 */
export function buildMapExtras(args: {
  basemap: MapLayerEntry
  overlays: RasterLayerEntry[]
  terrain3d: boolean
  theme: Theme
  keys: MapApiKeys
}): { extras: MapExtra[]; terrain: TerrainSpecification | null } {
  const { basemap, overlays, terrain3d, theme, keys } = args
  const extras: MapExtra[] = []
  if (terrain3d) {
    extras.push({ sourceId: TERRAIN_DEM_SOURCE_ID, source: buildTerrainDemSource(), layer: null })
    if (basemap.providesHillshade !== true) {
      extras.push({
        sourceId: HILLSHADE_DEM_SOURCE_ID,
        source: buildTerrainDemSource(),
        layer: buildHillshadeLayer(theme),
      })
    }
  }
  for (const overlay of overlays) {
    buildRasterSources(overlay, keys).forEach((source, i) => {
      const id = rasterLayerId(overlayLayerId(overlay.id), i)
      extras.push({ sourceId: id, source, layer: { id, type: 'raster', source: id } })
    })
  }
  if (terrain3d && basemap.kind === 'vector-style' && basemap.buildings != null) {
    extras.push({
      sourceId: basemap.buildings.source,
      source: null,
      layer: buildBuildingsLayer(basemap.buildings, theme),
    })
  }
  return { extras, terrain: terrain3d ? buildTerrainSpecification() : null }
}

/** Stable identity of a basemap style, so the map only reloads its style when this actually changes. */
export function baseStyleKey(entry: MapLayerEntry, theme: Theme): string {
  return entry.kind === 'vector-style' ? `${entry.id}:${theme}` : entry.id
}
